import BigNumber from 'bignumber.js'

import {
  formatCurrencyAmountNative,
  formatLiquidityShort,
  formatPnlUsd,
} from '@root/shared/core/format'

/**
 * How this screen says numbers.
 *
 * The app's own presets do the rounding — the same ones the rest of the
 * terminal uses, so a figure here and the same figure on the trade panel agree
 * to the digit. What is added is only the part a portfolio needs: a dash where
 * there is no answer, rather than a zero that reads as one.
 */

/**
 * Dollars, compact. Nothing for nothing known.
 *
 * The app's short format is built for liquidity and caps, and under a dollar
 * it rounds to `$0` — which on a token page reads as "no value" rather than
 * "a small one". A token at two and a half cents was printing its price as
 * zero while its chart was moving. So anything under a dollar is given in
 * significant digits instead.
 */
export const usd = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value)) {
    return '—'
  }
  const abs = Math.abs(value)
  if (abs !== 0 && abs < 1) {
    return `${value < 0 ? '-' : ''}$${significant(abs, 3)}`
  }
  return formatLiquidityShort(BigNumber(value))
}

/** Three digits that mean something, without the trailing zeros they carry. */
const significant = (abs: number, digits: number): string =>
  abs.toPrecision(digits).replace(/0+$/, '').replace(/\.$/, '')

const SUBSCRIPT = '₀₁₂₃₄₅₆₇₈₉'

/**
 * A price, which on these tokens is mostly zeros.
 *
 * Both terminals we are measured against write a price like $0.00000276 as
 * `$0.0₅276` — the subscript counts the zeros so the eye reads the magnitude
 * without counting them itself. Spelling it out instead costs a column and is
 * genuinely harder to read; rounding it, which is what we were doing, loses
 * the number altogether.
 */
export const price = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value)) {
    return '—'
  }
  if (value === 0) {
    return '$0'
  }
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  /* Above a thousand a price is a figure like any other; below it the compact
     format's rounding is the same fault again, one decimal place up. */
  if (abs >= 1000) {
    return usd(value)
  }
  if (abs >= 0.0001) {
    return `${sign}$${significant(abs, 4)}`
  }

  const [mantissa, exponent] = abs.toExponential(3).split('e')
  const zeros = -Number(exponent) - 1
  const digits = (mantissa ?? '').replace('.', '').replace(/0+$/, '') || '0'
  const counted = [...String(zeros)]
    .map((digit) => SUBSCRIPT[Number(digit)])
    .join('')

  return `${sign}$0.0${counted}${digits}`
}

/** Dollars with the sign kept, because a profit and a loss look alike at a glance. */
export const signedUsd = (value: number | undefined): string =>
  value === undefined || !Number.isFinite(value)
    ? '—'
    : formatPnlUsd(BigNumber(value))

/** The chain's own coin, in the app's own precision. */
export const native = (value: number | undefined): string =>
  value === undefined || !Number.isFinite(value)
    ? '—'
    : formatCurrencyAmountNative(BigNumber(value))

export const percent = (
  value: number | undefined,
  { sign = false }: { sign?: boolean } = {},
): string => {
  if (value === undefined || !Number.isFinite(value)) {
    return '—'
  }
  const rounded = Math.abs(value) >= 100 ? value.toFixed(0) : value.toFixed(1)
  const mark = sign && value > 0 ? '+' : ''
  return `${mark}${rounded}%`
}

export const count = (value: number | undefined): string =>
  value === undefined || !Number.isFinite(value)
    ? '—'
    : new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value)

/** A token amount, which can be enormous and is never a price. */
export const quantity = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value)) {
    return '—'
  }
  if (value === 0) {
    return '0'
  }
  if (Math.abs(value) < 0.001) {
    return '<0.001'
  }
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: Math.abs(value) < 1 ? 3 : 2,
  }).format(value)
}

/** An address, with enough of both ends to recognise it. */
export const shortAddress = (address: string, keep = 4): string =>
  address.length <= keep * 2 + 1
    ? address
    : `${address.slice(0, keep)}…${address.slice(-keep)}`

/** How long ago, in one unit. */
export const since = (at: number | undefined): string => {
  if (at === undefined || !Number.isFinite(at) || at <= 0) {
    return '—'
  }
  const seconds = Math.max(0, Math.floor((Date.now() - at) / 1000))
  if (seconds < 60) {
    return `${seconds}s`
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`
  }
  if (seconds < 86_400) {
    return `${Math.floor(seconds / 3600)}h`
  }
  return `${Math.floor(seconds / 86_400)}d`
}
